import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    Radio,
    DialogActions,
    Checkbox,
} from "@material-ui/core";
import DialogTitleDivider from "@icarius-common/dialogTitleDivider";
import PaperDraggable from "@icarius-common/paperDraggable";
import { CloseIcon } from "@icarius-icons";
import ButtonDialogAction from "@icarius-common/buttonDialogAction";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import UserSelectionColumn from "@icarius-common/userSelectionDialog/userSelectionColumn";
import SelectedUsersColumn from "@icarius-common/userSelectionDialog/selectedUsersColumn";
import useHandleUserSelection from "@icarius-common/userSelectionDialog/useHandleUserSelection";
import DialogTransition from "@icarius-common/dialogTransition";
import { getEvaluatorsForEvaluationProcessDefinitionAssignmentAction } from "../actions";
import { getLocalizedString } from "@icarius-localization/strings";

const EVALUATION_TYPES = [
    {
        key: "DES",
        name: "descendantEvaluatorDirectBoss",
        description: "willBeAssignedToManagers",
    },
    {
        key: "ASC",
        name: "ascendingEvaluator",
        description: "employeesUnderEvaluatedManagementDependentJobsLevel1",
    },
    {
        key: "PAR",
        name: "peerEvaluator",
        description: "coWorkersSameGroupsSameManagementSameFunctionOrSharingSamePosition",
    },
    {
        key: "FREE",
        name: "evaluatorSelectionWithFreeSelection",
        description: "youCanSelectEvaluatorFreely",
    },
];

const INITIAL_FILTERS = {
    matchPosition: false,
    matchFunction: false,
    matchManagment: false,
    matchGroups: false,
};

const EvaluatorDialog = (props) => {

    const {
        open,
        data,
        people,
        handleChange,
        handleClose,
        isReplace,
    } = props;

    const dispatch = useDispatch();

    const [step, setStep] = useState(0);
    const [typeSelected, setTypeSelected] = useState(!isReplace ? data.type : "FREE");
    const [filtersSelected, setFiltersSelected] = useState(INITIAL_FILTERS);

    const filteredPeople = useMemo(() => {
        if (typeSelected !== "PAR") return people;
        return people.filter((item) => Object.keys(INITIAL_FILTERS).some((filter) => item[filter] && filtersSelected[filter]));
    }, [typeSelected, people, filtersSelected])

    const {
        keysSelected,
        checkedList,
        handleSelection,
        resetUsersSelected,
    } = useHandleUserSelection(filteredPeople, [], true);

    const handleSubmit = () => {
        if (!typeSelected) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOption"), severity: "error", duration: 10000 }));
            return;
        }

        if (step === 1 && typeSelected !== 'DES' && !keysSelected?.length) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOption"), severity: "error", duration: 10000 }));
            return;
        }

        if (step === 0) {
            // si es descendiente, mando vacio
            if (typeSelected === 'DES') {
                handleChange(keysSelected, typeSelected);
                handleClose();
                return;
            }

            dispatch(getEvaluatorsForEvaluationProcessDefinitionAssignmentAction({
                collaborators: data.collaborators,
                type: typeSelected === 'FREE' ? '' : typeSelected,
            }))
                .then((resp) => {
                    if (resp.status === 200) {
                        setStep(1);
                    }
                });
            return;
        }

        handleChange(keysSelected, typeSelected);
        handleClose();
    }

    const handleCancel = () => {
        if (step === 0) {
            handleClose();
            return;
        }

        setStep(0);
    }

    const handleChangeType = (option) => {
        if (option !== typeSelected) {
            setTypeSelected(option);
            resetUsersSelected();
            setFiltersSelected(INITIAL_FILTERS);
        }
    }

    const handleSetFilter = (filterKey) => {
        setFiltersSelected((prev) => {
            return (
                {
                    ...prev,
                    [filterKey]: !prev[filterKey],
                }
            )
        });
        resetUsersSelected();
    }

    return (
        <Dialog
            TransitionComponent={DialogTransition}
            open={open}
            PaperComponent={PaperDraggable}
            maxWidth={'md'}
            fullWidth
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {
                    isReplace
                        ? getLocalizedString("newEvaluatorSelection")
                        : getLocalizedString("evaluatorSelectionForCollaborators", { type: data.typeString, length: data.collaborators.length })
                }
                <DialogTitleDivider />
            </DialogTitle>
            <CloseIcon className={"dialog-close-icon icon"} onClick={handleClose} />
            <DialogContent>
                <Typography className="whiteText" style={{ marginBottom: 20, fontSize: 18 }}>
                    {getLocalizedString("determineForSelectedCollaboratorsWhoWillBeTheirEvaluator")}
                </Typography>
                {
                    step === 1 && typeSelected === 'PAR' &&
                    <div style={{ display: 'flex', flexWrap: 'wrap', paddingBottom: 10 }}>
                        {
                            [
                                {
                                    key: 'matchPosition',
                                    name: getLocalizedString("position")
                                },
                                {
                                    key: 'matchFunction',
                                    name: getLocalizedString("function")
                                },
                                {
                                    key: 'matchManagment',
                                    name: getLocalizedString("management")
                                },
                                {
                                    key: 'matchGroups',
                                    name: getLocalizedString("workgroup")
                                },
                            ].map((item) => {
                                return (
                                    <div
                                        key={item.key}
                                        style={{ display: "flex", alignItems: "center", cursor: "pointer", paddingRight: 20 }}
                                        onClick={() => handleSetFilter(item.key)}
                                    >
                                        <Typography className="whiteText" variant="subtitle1" style={{ display: "inline-block" }}>
                                            {item.name}
                                        </Typography>
                                        <Checkbox checked={filtersSelected[item.key]} />
                                    </div>
                                )
                            })
                        }
                    </div>
                }
                <div style={{ maxHeight: 390, overflow: 'auto' }}>
                    {
                        step === 0 && EVALUATION_TYPES
                            .filter((item) => (!isReplace && (item.key === data.type || item.key === 'FREE')) || (isReplace && item.key === 'FREE'))
                            .map((item) => {
                                return (
                                    <div
                                        key={item.key}
                                        style={{
                                            cursor: 'pointer',
                                            display: 'flex',
                                            margin: '20px 0px'
                                        }}
                                        onClick={() => handleChangeType(item.key)}
                                    >
                                        <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <Typography className="whiteText" style={{ fontSize: 20 }}>
                                                    {getLocalizedString(item.name)}
                                                </Typography>
                                                <Typography className="whiteText">
                                                    {getLocalizedString(item.description)}
                                                </Typography>
                                            </div>
                                        </div>
                                        <Radio checked={item.key === typeSelected} />
                                    </div>
                                )
                            })
                    }
                    {
                        step === 1 && ['ASC', 'PAR', 'FREE'].includes(typeSelected) &&
                        <div style={{ display: 'flex', gap: 10 }}>
                            {/* Columna 1 - seleccionados */}
                            <SelectedUsersColumn
                                showAmount
                                title={getLocalizedString("selected")}
                                employees={checkedList}
                                handleClick={handleSelection}
                            />
                            {/* Columna 2 - todos los usuarios */}
                            <UserSelectionColumn
                                employees={checkedList}
                                handleClick={handleSelection}
                            />
                        </div>
                    }
                </div>
            </DialogContent>
            <DialogActions>
                <ButtonDialogAction onClick={handleCancel} text={step === 0 ? getLocalizedString("disagree") : getLocalizedString("goBack")} />
                <ButtonDialogAction onClick={handleSubmit} isAccept text={getLocalizedString("agree")} />
            </DialogActions>
        </Dialog>
    )
}

export default EvaluatorDialog;